import React from 'react'

import {
  Stack,
  Flex,
  Text,
  Icon,
  IconType,
  minWidths,
} from '@mindfulchefuk/design-system'

import styled from '@emotion/styled'
import { CMSImage } from '@mindfulchefuk/features/CMS/components/CMSImage'
import { useCMSPageBanner } from '@mindfulchefuk/features/CMS/hooks/useCMSPageBanner'
import { useCMSBannerRedirect } from '@mindfulchefuk/features/CMS/hooks/useCMSBannerRedirect'
import { CMSBannerLinkWrapper } from '@mindfulchefuk/features/CMS/components/CMSBannerLinkWrapper'
import { PageSource } from '@mindfulchefuk/analytics'
import { setDeliveryDate } from '@mindfulchefuk/actions/basketActions'
import { useDispatch } from 'react-redux'
import { trackAnalyticsEvent } from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'
import { dateFormats } from '@mindfulchefuk/lib/dateFormats'

const Item = ({
  content,
  iconType,
  id,
}: {
  content: string
  iconType: IconType
  id: string
}) => {
  return (
    <Stack direction="row" spacing={10} id={id}>
      <Icon
        type={iconType}
        color="aubergine"
        size={{ base: 24, md: 28 }}
        flexShrink={0}
      />
      <Text
        variant={{ base: 'body-sm', md: 'body-lg' }}
        color="pepper"
        ml={[0, 8, 16]}
        id={`${id}-copy`}
      >
        {content}
      </Text>
    </Stack>
  )
}

const BannerImage = styled(CMSImage)`
  width: 100%;
  height: 100%;

  @media ${minWidths.sm} {
    object-fit: cover;
    position: absolute;
    left: 0;
    top: 0;
  }
`

export const ChooseRecipesBanner = (): React.ReactElement => {
  const dispatch = useDispatch()
  const banner = useCMSPageBanner({ page: 'page-recipe-onboarding' })
  const { redirectURL, targetDeliveryDate } = useCMSBannerRedirect(
    banner?.redirectConfig
  )

  const defaultImageSrc = 'defaults/choose-recipes-banner.jpg'

  return (
    <Flex
      flexDirection={{ base: 'column', sm: 'row' }}
      id="choose-recipes-header"
    >
      <CMSBannerLinkWrapper
        href={redirectURL}
        width="100%"
        position="relative"
        overflow="hidden"
        onClick={() => {
          trackAnalyticsEvent({
            name: 'choose recipes banner clicked',
            data: {
              'banner name': banner?.title,
              'campaign name': banner?.campaign ? banner?.campaign.title : '',
              'page source': 'choose recipes page' as PageSource,
              'customer preferences': [''],
            },
          })
          dispatch(setDeliveryDate(dateFormats.iso(targetDeliveryDate)))
        }}
      >
        <BannerImage
          src={banner?.image.src || defaultImageSrc}
          alt={banner?.image.altText || 'Choose Recipes'}
          width={640}
          sources={banner?.image.sources || []}
          data-testid="choose-recipes-header-img"
        />
      </CMSBannerLinkWrapper>
      <Stack
        backgroundColor="salt"
        justifyContent="center"
        paddingY={{ base: 16, lg: 24 }}
        paddingX={[24, 16, 16, 24]}
        spacing={{ base: 12, lg: 36 }}
        minWidth={['auto', 240, '398px', '526px']}
      >
        <Item
          id="choose-recipes-text-protein"
          iconType="heart"
          content="Delicious, high quality protein - and lots of it"
        />
        <Item
          id="choose-recipes-text-refined-carbs"
          iconType="gluten-free"
          content="No stodgy refined carbs - no carb comas here"
        />
        <Item
          id="choose-recipes-text-fibre"
          iconType="soil"
          content="Goodness from natural food. Plenty of veg - full of fibre"
        />
      </Stack>
    </Flex>
  )
}
